import { useEffect } from "react";

type Props = {
  gaId?: string;
};

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const GoogleTagManager = ({ gaId: propGaId }: Props) => {
  // Use prop GA ID or fallback to hardcoded value
  const gaId = propGaId || "G-RR8DZE0TLP";
  
  useEffect(() => {
    console.log("🔍 GoogleTagManager: Initializing...");

    if (!gaId) {
      console.warn("⚠️ GoogleTagManager: GA ID not provided - Analytics disabled");
      return;
    }

    console.log(`✅ GoogleTagManager: Using GA ID: ${gaId}`);

    try {
      // Check if already initialized by looking for GA script
      const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${gaId}"]`);
      if (existingScript && window.dataLayer?.length > 0) {
        console.log("ℹ️ GoogleTagManager: Already initialized, skipping...");
        return;
      }

      console.log("🚀 GoogleTagManager: Setting up dataLayer and gtag function...");

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];

      // Create enhanced gtag function with logging
      window.gtag = function () {
        const args = Array.from(arguments);
        console.log("📊 GA Event:", args);
        window.dataLayer.push(arguments);
      };

      console.log("⚙️ GoogleTagManager: Configuring GA with timestamp...");

      // Initialize GA with timestamp
      const initTime = new Date();
      window.gtag("js", initTime);
      window.gtag("config", gaId, {
        // Enable enhanced measurement
        send_page_view: true,
        // Add debug info
        debug_mode: process.env.NODE_ENV === "development"
      });

      console.log(`📈 GoogleTagManager: GA configured at ${initTime.toISOString()}`);

      // Add GA script with enhanced logging
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;

      script.onload = () => {
        console.log("✅ GoogleTagManager: GA script loaded successfully");
        console.log(`📊 GoogleTagManager: DataLayer entries: ${window.dataLayer?.length || 0}`);

        // Log current dataLayer content
        if (window.dataLayer?.length > 0) {
          console.log("📋 GoogleTagManager: Current dataLayer:", window.dataLayer.slice(-3));
        }
      };

      script.onerror = (error) => {
        console.error("❌ GoogleTagManager: Failed to load GA script:", error);
        console.error(`❌ GoogleTagManager: Script URL: ${script.src}`);
      };

      console.log(`🔗 GoogleTagManager: Loading script from: ${script.src}`);
      document.head.appendChild(script);

      // Set up periodic status logging (only in development)
      let statusInterval: NodeJS.Timeout | null = null;
      if (process.env.NODE_ENV === "development") {
        statusInterval = setInterval(() => {
          console.log(`📊 GA Status: DataLayer has ${window.dataLayer?.length || 0} entries`);
        }, 30000); // Log every 30 seconds
      }

      return () => {
        console.log("🧹 GoogleTagManager: Cleaning up...");
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
        if (statusInterval) {
          clearInterval(statusInterval);
        }
      };
    } catch (error) {
      console.error("❌ GoogleTagManager: Error during initialization:", error);
      console.error("❌ GoogleTagManager: Stack trace:", error instanceof Error ? error.stack : "No stack trace");
    }
  }, [gaId]);

  return null;
};

export default GoogleTagManager;
